import { useRef, useState, useEffect } from "react";
import { Loader2, Save, Plus, Trash2, Star, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MediaUpload } from "@/components/site/MediaUpload";
import { toast } from "sonner";

function F({ label, children }) { return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1">{children}</div></div>; }
const inp = "bg-zinc-900 border-white/10 text-white";

/* ---------------- INSTRUCTOR ---------------- */
export function InstructorEditor() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/catalog/homepage").then(({ data }) => setF(data.instructor || {})); }, []);
  if (!f) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const hl = f.highlights || [];
  const setHl = (i, v) => { const a = [...hl]; a[i] = v; set("highlights", a); };
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/homepage", { instructor: f }); toast.success("Instructor saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 rounded-lg border border-white/10 bg-zinc-900/40 p-5">
      <F label="Eyebrow"><Input data-testid="inst-eyebrow" value={f.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className={inp} /></F>
      <F label="Title"><Input data-testid="inst-title" value={f.title || ""} onChange={(e) => set("title", e.target.value)} className={inp} /></F>
      <F label="Bio (paragraph 1)"><Textarea rows={3} data-testid="inst-bio1" value={f.bio1 || ""} onChange={(e) => set("bio1", e.target.value)} className={inp} /></F>
      <F label="Bio (paragraph 2)"><Textarea rows={3} value={f.bio2 || ""} onChange={(e) => set("bio2", e.target.value)} className={inp} /></F>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Experience Years (badge)"><Input value={f.experience_years || ""} onChange={(e) => set("experience_years", e.target.value)} className={inp} /></F>
        <F label="Instagram URL"><Input value={f.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={inp} placeholder="https://instagram.com/..." /></F>
      </div>
      <MediaUpload label="Instructor Photo" value={f.image} onChange={(v) => set("image", v)} kind="image" />
      <div>
        <Label className="text-xs uppercase text-zinc-400">Skill Highlights</Label>
        <div className="mt-1 space-y-2">
          {hl.map((h, i) => (
            <div key={i} className="flex gap-2">
              <Input value={h} onChange={(e) => setHl(i, e.target.value)} className={inp} />
              <Button variant="ghost" onClick={() => set("highlights", hl.filter((_, x) => x !== i))} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => set("highlights", [...hl, ""])} className="border-white/15 text-white"><Plus className="mr-1 h-3 w-3" /> Add Highlight</Button>
        </div>
      </div>
      <div className="flex items-center justify-between rounded border border-white/10 px-3 py-2">
        <span className="text-sm text-zinc-300">Show email contact button</span>
        <Switch data-testid="inst-show-email" checked={f.show_email !== false} onCheckedChange={(v) => set("show_email", v)} />
      </div>
      <Button data-testid="save-instructor-btn" onClick={save} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Instructor</>}</Button>
    </div>
  );
}

/* ---------------- STUDENT RESULTS ---------------- */
export function StudentResultsEditor() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/student-results").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const add = async () => { await api.post("/admin/student-results", { name: "New Student" }); load(); };
  const upd = async (id, patch) => { await api.put(`/admin/student-results/${id}`, patch); load(); };
  const del = async (id) => { if (confirm("Delete this entry?")) { await api.delete(`/admin/student-results/${id}`); load(); } };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const arr = [...items]; [arr[i], arr[j]] = [arr[j], arr[i]];
    setItems(arr); await api.post("/admin/student-results/reorder", { order: arr.map((x) => x.id) });
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-zinc-400">Admin-managed student haircut results.</p>
        <Button data-testid="add-result-btn" onClick={add} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Plus className="mr-1 h-4 w-4" /> Add Result</Button>
      </div>
      <div className="space-y-4" data-testid="results-editor">
        {items.length === 0 && <p className="rounded border border-white/10 p-6 text-center text-zinc-500">No student results yet. Add your first one.</p>}
        {items.map((r, i) => (
          <div key={r.id} data-testid={`result-item-${r.id}`} className={`rounded-lg border ${r.visible === false ? "opacity-60 border-white/5" : "border-white/10"} bg-zinc-900/40 p-4`}>
            <div className="grid gap-4 md:grid-cols-[160px_1fr]">
              <MediaUpload label="Result Image" value={r.image} onChange={(v) => upd(r.id, { image: v })} kind="image" />
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <F label="Student Name"><Input value={r.name || ""} onChange={(e) => upd(r.id, { name: e.target.value })} className={inp} /></F>
                  <F label="Instagram (@handle or URL)"><Input value={r.instagram || ""} onChange={(e) => upd(r.id, { instagram: e.target.value })} className={inp} /></F>
                </div>
                <F label="Testimonial"><Textarea rows={2} value={r.testimonial || ""} onChange={(e) => upd(r.id, { testimonial: e.target.value })} className={inp} /></F>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch checked={!!r.featured} onCheckedChange={(v) => upd(r.id, { featured: v })} /> <Star className="h-4 w-4 text-[#DCA846]" /> Featured</label>
                  <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid={`result-visible-${r.id}`} checked={r.visible !== false} onCheckedChange={(v) => upd(r.id, { visible: v })} /> {r.visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4 text-[#DCA846]" />} Visible</label>
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} className="text-zinc-400"><ArrowUp className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="text-zinc-400"><ArrowDown className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" data-testid={`delete-result-${r.id}`} onClick={() => del(r.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- CARD / CAROUSEL MANAGER ---------------- */
const CARD_SECTIONS = [
  { key: "featured", label: "Featured Courses" },
  { key: "popular", label: "Popular Right Now" },
  { key: "free", label: "Free Lessons" },
  { key: "new", label: "New Releases" },
];

export function CardsEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/homepage").then(({ data }) => setContent(data.content || {})); }, []);
  if (!content) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const setC = (key, field, val) => setContent((c) => ({ ...c, [key]: { ...(c[key] || {}), [field]: val } }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/homepage", { content }); toast.success("Card layouts saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const Sel = ({ v, onC, opts, tid }) => (
    <select data-testid={tid} value={v} onChange={(e) => onC(e.target.value)} className="mt-1 w-full rounded bg-zinc-900 border border-white/10 px-2 py-2 text-sm text-white">
      {opts.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
    </select>
  );
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-400">Control how course cards display in each section. Reviews stay as a carousel.</p>
      {CARD_SECTIONS.map((s) => {
        const c = content[s.key] || {};
        return (
          <div key={s.key} data-testid={`cards-item-${s.key}`} className="rounded-lg border border-white/10 bg-zinc-900/40 p-4">
            <p className="mb-3 font-display uppercase tracking-wide text-white">{s.label}</p>
            <div className="grid gap-3 sm:grid-cols-4">
              <div><Label className="text-xs uppercase text-zinc-400">Layout</Label>
                <Sel tid={`cards-layout-${s.key}`} v={c.layout || "carousel"} onC={(v) => setC(s.key, "layout", v)} opts={[["carousel", "Carousel"], ["grid", "Grid"]]} /></div>
              <div><Label className="text-xs uppercase text-zinc-400">Max Shown</Label>
                <Input data-testid={`cards-max-${s.key}`} type="number" min="1" value={c.max ?? ""} onChange={(e) => setC(s.key, "max", e.target.value ? Number(e.target.value) : "")} className={inp + " mt-1"} placeholder="All" /></div>
              <div><Label className="text-xs uppercase text-zinc-400">Desktop Cols</Label>
                <Sel v={String(c.cols_desktop || 3)} onC={(v) => setC(s.key, "cols_desktop", Number(v))} opts={[["2", "2"], ["3", "3"], ["4", "4"]]} /></div>
              <div><Label className="text-xs uppercase text-zinc-400">Mobile Cols</Label>
                <Sel v={String(c.cols_mobile || 2)} onC={(v) => setC(s.key, "cols_mobile", Number(v))} opts={[["1", "1"], ["2", "2"]]} /></div>
            </div>
          </div>
        );
      })}
      <Button data-testid="save-cards-btn" onClick={save} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Layouts</>}</Button>
    </div>
  );
}

/* ---------------- MEDIA LIBRARY ---------------- */
export function MediaLibraryEditor() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/media").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const setAlt = async (id, alt) => { await api.put(`/admin/media/${id}`, { alt_text: alt }); };
  const del = async (id) => { if (confirm("Delete this image from the library?")) { await api.delete(`/admin/media/${id}`); load(); } };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-zinc-400">All uploaded images. Reuse them anywhere via the "Library" picker.</p>
        <MediaUpload label="" onChange={() => load()} kind="image" testid="library-upload" />
      </div>
      {items.length === 0 ? <p className="rounded border border-white/10 p-8 text-center text-zinc-500">No images uploaded yet.</p> :
        <div data-testid="media-library" className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {items.map((f) => (
            <div key={f.id} data-testid={`media-${f.id}`} className="rounded-lg border border-white/10 bg-zinc-900/40 p-2">
              <div className="aspect-square overflow-hidden rounded"><img src={resolveImg(f.storage_path)} alt={f.alt_text || ""} className="h-full w-full object-cover" /></div>
              <Input defaultValue={f.alt_text || ""} onBlur={(e) => setAlt(f.id, e.target.value)} placeholder="Alt text" className={inp + " mt-2 text-xs"} data-testid={`media-alt-${f.id}`} />
              <Button size="sm" variant="ghost" onClick={() => del(f.id)} className="mt-1 w-full text-red-400" data-testid={`media-delete-${f.id}`}><Trash2 className="mr-1 h-3 w-3" /> Delete</Button>
            </div>
          ))}
        </div>}
    </div>
  );
}

export function FooterEditor() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setF(data || {})); }, []);
  if (!f) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { footer_description: f.footer_description, youtube_url: f.youtube_url, copyright_text: f.copyright_text, contact_email: f.contact_email, instagram_url: f.instagram_url }); toast.success("Footer saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 rounded-lg border border-white/10 bg-zinc-900/40 p-5">
      <F label="Footer Description"><Textarea rows={3} data-testid="footer-description" value={f.footer_description || ""} onChange={(e) => set("footer_description", e.target.value)} className={inp} /></F>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Contact Email"><Input value={f.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inp} placeholder="you@epicfade.com" /></F>
        <F label="Instagram URL"><Input value={f.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className={inp} placeholder="https://instagram.com/..." /></F>
        <F label="YouTube URL"><Input data-testid="footer-youtube-input" value={f.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} className={inp} placeholder="https://youtube.com/@..." /></F>
        <F label="Copyright Text"><Input value={f.copyright_text || ""} onChange={(e) => set("copyright_text", e.target.value)} className={inp} placeholder="© 2026 Epic Fade Academy. All rights reserved." /></F>
      </div>
      <p className="text-xs text-zinc-500">Social icons and links appear in the footer only when a value is set. Navigation & legal links stay automatic.</p>
      <Button data-testid="save-footer-btn" onClick={save} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Footer</>}</Button>
    </div>
  );
}
